// Shared tokens + reusable atoms for the Medical Professional Portal.
//
// The palette and the component shapes here are AIMS's. AIMS (the sibling
// QMS product) is the design the business wants the internal surfaces to
// read as, so its token set is reproduced below value-for-value from
// aims2.0/frontend/src/styles/aims.css `:root`. Keeping the same KEY names
// as the old teal/sage palette is deliberate: ~1,000 call sites across the
// medical, lab, corporate and feedback portals build their own components
// out of `MP.*`, so remapping the values restyles all of them at once
// without touching a single screen.
//
// Where a key had no AIMS counterpart it aliases the nearest one
// (`yellow`/`orange` → AIMS amber), so no call site renders an undefined
// colour.

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export const MP = {
  // ── Teal. AIMS's one brand colour; dark enough to carry white text,
  //    which the old #00B49A mint was not.
  teal:       '#0F766E',  // --teal / --rail
  tealDark:   '#0C5F58',  // .btn-teal:hover
  tealLight:  '#E6F2F1',  // --teal-bg
  tealMid:    '#A8CCC8',  // no AIMS equivalent; borders + muted icons

  // ── Ink scale. Body copy is near-black, not dark green.
  dark:       '#10151C',  // --ink      headings
  darkMid:    '#4A5763',  // --ink-2
  text:       '#10151C',  // --ink      table cells
  textMuted:  '#4A5763',  // --ink-2    secondary copy
  ink3:       '#7E8A96',  // --ink-3    mono micro-labels

  // ── Paper and rules.
  white:      '#FFFFFF',  // --surface
  bg:         '#F4F6F8',  // --canvas
  border:     '#DDE3E9',  // --rule     card + header rules
  borderSoft: '#EDF1F4',  // --rule-2   row separators
  rowHover:   '#FAFCFD',  // table.tbl tbody tr:hover

  // ── Accents, each with the tint AIMS pairs it with.
  red:        '#B3261E', redLight:    '#FBEAE9',
  amber:      '#B4700B', amberLight:  '#FCF1DF',
  yellow:     '#B4700B', yellowLight: '#FCF1DF',  // alias of amber
  orange:     '#B4700B', orangeLight: '#FCF1DF',  // alias of amber
  green:      '#1E7A45', greenLight:  '#E7F3EC',
  blue:       '#1B5E9E', blueLight:   '#E8F1F9',
  purple:     '#6B4E9B', purpleLight: '#F0EBF7',
  grey:       '#5B6874', greyLight:   '#EDF0F3',
  slate:      '#334A63', slateLight:  '#EEF2F8',

  // ── Card chrome. AIMS cards wear a dark teal title bar under a 2px
  //    gold rule, and table headers a 1px pink one. Those two lines are
  //    the most recognisable thing about the design.
  hd:         '#20605B',  // --hd      card title bar
  hdFg:       '#FFFFFF',  // --hd-fg
  lnTop:      '#F4C430',  // --ln-top  under the title bar
  lnBot:      '#F94C8E',  // --ln-bot  under a table header
  gold:       '#FFC81F',  // --gold

  radius:     '6px',      // --r
  shadow:     '0 1px 2px rgba(16,21,28,.06), 0 4px 12px rgba(16,21,28,.05)',
  focus:      '0 0 0 3px rgba(15,118,110,.12)',
};

// Both families are Google fonts fetched at runtime and re-declared under
// private names by loadMedicalPortalFont() in MedicalDashboard.js —
// IBM Plex Sans as 'MedicalPortalSans', IBM Plex Mono as
// 'MedicalPortalMono'. Private names so the rest of the site, which asks
// for 'Outfit', is untouched.
//
// MONO CARRIES `!important`, AND MUST. The portal forces its sans family
// onto every element (that is the only way to reach MUI's portalled
// Modal / Menu / Tooltip surfaces, which render outside the wrapper and so
// can't be hit by an `& *` sx selector). That rule is wrapped in `:where()`
// so it contributes NO specificity — which is what lets this `!important`
// value, set through sx, win and keep a mono cell mono. Drop the
// `!important` and all 179 `fontFamily: MONO` sites silently render sans.
export const DISPLAY = "'MedicalPortalSans', system-ui, -apple-system, sans-serif";
export const MONO = "'MedicalPortalMono', ui-monospace, SFMono-Regular, monospace !important";

// ---------- Cloud Function row helpers ----------
// The `getSalesDataByCustomer` endpoint returns one row per SERVICE.
// These helpers are shared by Overview.js ("My Reports" table) and
// PatientReports.js (full listing) so both surfaces format data the
// same way.

// A row's billed amount. Not every location sends `NetAmt` — some emit a
// reduced schema carrying only `ServiceGrossAmt`, which used to render those
// tests as ₹0 and understate the visit total. Cancelled tests (negative
// amounts) are already filtered server-side by the report loader.
const rowAmount = (r) => {
  for (const f of ['NetAmt', 'ServiceGrossAmt']) {
    const v = r?.[f];
    if (v === undefined || v === null || v === '') continue;
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return 0;
};

// Whether the lab has RELEASED this test's report — the only trustworthy
// readiness signal in the feed. `ReportReleased` is 1 when the report is
// signed off and available, 0 while it's still being processed.
//
// Returns `null` when the field isn't on the row at all. That matters: not
// every source populates it, and a missing field must NOT be read as "not
// ready" or those tests would become permanently unopenable. Callers treat
// null as ready, which is exactly how the portal behaved before this existed.
//
// Don't be tempted to use `Status` here — in this feed it carries priority
// words ("Normal"/"Urgent"), not approval state.
export const reportReleasedOf = (r) => {
  const v = r?.ReportReleased ?? r?.reportReleased ?? r?.REPORTRELEASED;
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'boolean') return v;
  return /^(1|y|yes|true|released)$/i.test(String(v).trim());
};

// Rows come in order per service. Same VisitNo groups into one visit
// with a list of tests. First row wins for patient/visit metadata
// (they're identical across a visit anyway).
export const groupByVisit = (rows = []) => {
  const map = new Map();
  for (const r of rows) {
    const key = r.VisitNo || r.BILLNO || r.SinnNo;
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, {
        visitNo: r.VisitNo,
        billNo:  r.BILLNO,
        uhid:    r.UHID,
        patient: cleanName(r.PATIENTNAME),
        age:     cleanAge(r.Age),
        gender:  r.Gender,
        billDate: r.billdate,
        openDate: r.OpenDate,
        referral: r.REFERALDOCTOR || '—',
        lab:      r.TaggedLabName || r.PCCName,
        city:     r.City,
        status:   r.Status,
        department: r.DEPARTMENT,
        tests: [],
        totalAmount: 0,
      });
    }
    const visit = map.get(key);
    const amount = rowAmount(r);
    visit.tests.push({
      id:     r.serviceid,
      name:   r.SERVICENAME,
      dept:   r.DEPARTMENT,
      amount,
      // null = the feed didn't say; the modal treats that as ready.
      released: reportReleasedOf(r),
    });
    visit.totalAmount += amount;
  }
  return Array.from(map.values());
};

// "Mrs. JASBIR KAUR" → "Mrs. Jasbir Kaur" — title-case but preserve
// the honorific prefix.
export const cleanName = (raw = '') => {
  const parts = String(raw).trim().split(/\s+/);
  return parts
    .map((p) => {
      if (/^(mr|mrs|ms|mx|dr|prof)\.?$/i.test(p)) {
        const base = p.replace('.', '');
        return base.charAt(0).toUpperCase() + base.slice(1).toLowerCase() + '.';
      }
      return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
    })
    .join(' ');
};

// "29 Y 0 M 0 D " → "29 yrs"
export const cleanAge = (raw = '') => {
  const m = String(raw).match(/(\d+)\s*Y/i);
  if (m) return `${m[1]} yrs`;
  const mo = String(raw).match(/(\d+)\s*M/i);
  if (mo) return `${mo[1]} mo`;
  return String(raw).trim() || '—';
};

// "02-Apr-2026" → "Apr 2, 2026"
export const prettyDate = (raw = '') => {
  const m = String(raw).match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})/);
  if (!m) return String(raw).trim();
  return `${m[2]} ${Number(m[1])}, ${m[3]}`;
};

// ---------- Status / priority helpers ----------
// Tone per state, using AIMS's chip pairs. "In progress" is BLUE here, not
// orange: AIMS reserves amber for things that need attention (pending,
// overdue) and uses blue for work that is simply underway, so an
// investigating query no longer looks like a warning.
export const statusStyle = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('open')) return { bg: MP.redLight, fg: MP.red };
  // Trust OPM complaint labels: Received → With department → In progress
  if (s.includes('received')) return { bg: MP.amberLight, fg: MP.amber };
  if (s.includes('department')) return { bg: MP.blueLight, fg: MP.blue };
  if (s.includes('progress') || s.includes('investigat')) return { bg: MP.blueLight, fg: MP.blue };
  if (s.includes('resolv')) return { bg: MP.greenLight, fg: MP.green };
  if (s.includes('closed')) return { bg: MP.greyLight, fg: MP.grey };
  if (s.includes('pending')) return { bg: MP.amberLight, fg: MP.amber };
  if (s.includes('normal') || s.includes('ready')) return { bg: MP.tealLight, fg: MP.teal };
  if (s.includes('urgent') || s.includes('stat')) return { bg: MP.redLight, fg: MP.red };
  return { bg: MP.greyLight, fg: MP.grey };
};

export const priorityColor = (p) => {
  const v = (p || '').toLowerCase();
  if (v.startsWith('h')) return MP.red;
  if (v.startsWith('m')) return MP.amber;
  return MP.green;
};

// ---------- Atoms ----------
// AIMS `.chip` — a pill carrying a 5px dot in its own text colour. The dot
// is what makes a row of chips readable at a glance without reading them,
// and it comes free from `currentColor`, so a new tone needs no extra rule.
const chipSx = (bg, fg, { dot = true, mono = false } = {}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  px: '8px',
  py: '2px',
  borderRadius: '20px',
  fontSize: 11,
  fontWeight: mono ? 600 : 500,
  lineHeight: 1.6,
  whiteSpace: 'nowrap',
  bgcolor: bg,
  color: fg,
  ...(mono ? { fontFamily: MONO } : null),
  ...(dot
    ? {
      '&::before': {
        content: '""',
        width: 5,
        height: 5,
        borderRadius: '50%',
        bgcolor: 'currentColor',
        flex: 'none',
      },
    }
    : null),
});

export const StatusBadge = ({ children }) => {
  const { bg, fg } = statusStyle(children);
  return <Box component="span" sx={chipSx(bg, fg)}>{children}</Box>;
};

export const PriorityDot = ({ level }) => (
  <Box
    component="span"
    sx={{
      width: 6,
      height: 6,
      borderRadius: '50%',
      display: 'inline-block',
      mr: 0.75,
      bgcolor: priorityColor(level),
      verticalAlign: 'middle',
    }}
  />
);

// Chip-style pill used in TAT cells & meta rows. Dotless and monospaced —
// these mostly hold numbers and codes, and AIMS sets those in IBM Plex Mono
// with tabular figures so columns of them line up.
export const Pill = ({ children, tone = 'teal' }) => {
  const tones = {
    teal:   [MP.tealLight, MP.teal],
    green:  [MP.greenLight, MP.green],
    yellow: [MP.amberLight, MP.amber],
    amber:  [MP.amberLight, MP.amber],
    orange: [MP.amberLight, MP.amber],
    red:    [MP.redLight, MP.red],
    blue:   [MP.blueLight, MP.blue],
    purple: [MP.purpleLight, MP.purple],
    grey:   [MP.greyLight, MP.grey],
  };
  const [bg, fg] = tones[tone] || tones.teal;
  return (
    <Box component="span" sx={{ ...chipSx(bg, fg, { dot: false, mono: true }), fontVariantNumeric: 'tabular-nums' }}>
      {children}
    </Box>
  );
};

// ---------- Page layout helpers ----------
// AIMS `.card-x`.
export const SectionCard = ({ children, sx = {} }) => (
  <Box
    sx={{
      bgcolor: MP.white,
      border: `1px solid ${MP.border}`,
      borderRadius: MP.radius,
      boxShadow: MP.shadow,
      overflow: 'hidden',
      ...sx,
    }}
  >
    {children}
  </Box>
);

// AIMS `.card-x .hd` — a dark teal title bar sitting under a 2px rule.
//
// `accent` used to tint a pale gradient behind the title. It now colours
// that rule instead: the bar is always the same dark teal (a per-section
// background colour fights the chips inside the card), and the line under
// it is what distinguishes one section from the next. Default is AIMS's
// gold. Anything in `right` renders ON the dark bar, so it has to be
// light-on-dark — the two sx blocks below handle the ordinary cases.
export const CardHeader = ({ title, subtitle, right, accent = MP.lnTop }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    alignItems={{ xs: 'flex-start', sm: 'center' }}
    justifyContent="space-between"
    spacing={1}
    sx={{
      px: '15px',
      py: '9px',
      minHeight: 48,
      bgcolor: MP.hd,
      borderBottom: `2px solid ${accent}`,
      // NOTE: no blanket colour for buttons or icons in here. A
      // `& .MuiButtonBase-root` rule used to force them light, and because
      // a descendant selector outranks the child's own sx, it beat any
      // button that styled itself — the white Export button came out with
      // a white label and only became readable on hover, where a second
      // rule happened to win. Anything passed as `right` renders ON the
      // dark bar and colours itself accordingly.
    }}
  >
    <Box>
      <Typography sx={{ fontFamily: DISPLAY, fontSize: 13.5, fontWeight: 700, letterSpacing: '.02em', color: MP.hdFg }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,.82)', mt: 0.25 }}>{subtitle}</Typography>
      )}
    </Box>
    {right}
  </Stack>
);

// AIMS `.tile` — a compact stat block with the accent on its left edge.
// Deliberately much smaller than the 42px display figure this replaced:
// AIMS puts four to six of these in a row above a register, and the number
// is set in mono at 21px so a row of them stays scannable.
export const StatCard = ({ label, value, sub, tone = 'teal' }) => {
  const tones = {
    teal:   MP.teal,
    yellow: MP.amber,
    amber:  MP.amber,
    orange: MP.amber,
    red:    MP.red,
    green:  MP.green,
    blue:   MP.blue,
    purple: MP.purple,
    grey:   MP.grey,
  };
  const accent = tones[tone] || MP.teal;
  return (
    <Box
      sx={{
        bgcolor: MP.white,
        border: `1px solid ${MP.border}`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: MP.radius,
        px: '13px',
        py: '12px',
        height: '100%',
      }}
    >
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: 10,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: MP.ink3,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: 21,
          fontWeight: 600,
          letterSpacing: '-.02em',
          lineHeight: 1,
          mt: '4px',
          color: MP.dark,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
      {sub && <Typography sx={{ fontSize: 11.5, color: MP.textMuted, mt: '4px' }}>{sub}</Typography>}
    </Box>
  );
};

// Alert block (info/warning/success). Tinted panel with the accent on its
// left edge, the same device as `.tile`, so a notice and a stat read as
// members of one family.
export const Alert = ({ tone = 'info', children, sx = {} }) => {
  const tones = {
    info:    [MP.tealLight, MP.teal],
    warning: [MP.amberLight, MP.amber],
    success: [MP.greenLight, MP.green],
    error:   [MP.redLight, MP.red],
  };
  const [bg, fg] = tones[tone] || tones.info;
  return (
    <Box
      sx={{
        bgcolor: bg,
        color: fg,
        borderLeft: `3px solid ${fg}`,
        borderRadius: MP.radius,
        px: '13px',
        py: '11px',
        fontSize: 12.5,
        lineHeight: 1.55,
        display: 'flex',
        gap: 1.25,
        alignItems: 'flex-start',
        mb: 2,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// AIMS `.tabline button` — underlined tabs, not boxed ones. The old version
// drew a folder tab by painting its bottom border the card colour, which
// only worked when the tab row sat directly on a white card.
export const TabButton = ({ active, onClick, children, badge, badgeTone = 'teal' }) => {
  const badgeTones = {
    teal: [MP.borderSoft, MP.ink3],
    red:  [MP.redLight, MP.red],
  };
  const [bBg, bFg] = badgeTones[badgeTone] || badgeTones.teal;
  return (
    <Box
      onClick={onClick}
      sx={{
        px: '14px',
        py: '9px',
        bgcolor: 'transparent',
        border: 0,
        borderBottom: `2px solid ${active ? MP.teal : 'transparent'}`,
        cursor: 'pointer',
        color: active ? MP.teal : MP.textMuted,
        fontWeight: active ? 600 : 500,
        fontSize: 13,
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        userSelect: 'none',
        transition: 'color .15s, border-color .15s',
        whiteSpace: 'nowrap',
        '&:hover': { color: active ? MP.teal : MP.dark },
      }}
    >
      {children}
      {badge != null && (
        <Box
          component="span"
          sx={{
            bgcolor: bBg,
            color: bFg,
            fontFamily: MONO,
            fontSize: 10.5,
            px: '5px',
            py: '1px',
            borderRadius: '6px',
          }}
        >
          {badge}
        </Box>
      )}
    </Box>
  );
};

// AIMS `.flabel` — mono, 10px, widely tracked, tertiary ink. Quieter than
// the old 11px bold near-black, which competed with the value under it.
export const FieldLabel = ({ children, required }) => (
  <Typography
    sx={{
      fontFamily: MONO,
      fontSize: 10,
      letterSpacing: '.1em',
      textTransform: 'uppercase',
      color: MP.ink3,
      mb: '6px',
    }}
  >
    {children}
    {required && <Box component="span" sx={{ color: MP.red, ml: 0.25 }}>*</Box>}
  </Typography>
);

// Re-usable table wrapper (sticky header, horizontal scroll).
//
// `border-collapse: separate` is required, not cosmetic: a collapsed table
// drops the inset box-shadow that draws the pink rule under the header, and
// loses the header's own background once it goes sticky.
export const ScrollTable = ({ children, maxH = 380, minW = 960 }) => (
  <Box sx={{ overflowX: 'auto', overflowY: 'auto', overscrollBehavior: 'contain', maxHeight: maxH }}>
    <Box
      component="table"
      sx={{
        width: '100%',
        minWidth: minW,
        borderCollapse: 'separate',
        borderSpacing: 0,
        fontSize: 13,
      }}
    >
      {children}
    </Box>
  </Box>
);

// AIMS `table.tbl thead th`. The rule under the header is an INSET SHADOW,
// not a border: a border would add to the header's height and, once the
// header is sticky, would scroll out from under it.
export const Th = ({ children, align = 'left', sx = {} }) => (
  <Box
    component="th"
    sx={{
      position: 'sticky',
      top: 0,
      zIndex: 2,
      bgcolor: MP.white,
      boxShadow: `inset 0 -1px 0 ${MP.lnBot}`,
      textAlign: align,
      fontFamily: MONO,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '.02em',
      textTransform: 'uppercase',
      color: MP.dark,
      px: '11px',
      pt: '11px',
      pb: '9px',
      whiteSpace: 'nowrap',
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const Td = ({ children, sx = {} }) => (
  <Box
    component="td"
    sx={{
      px: '11px',
      py: '9px',
      borderBottom: `1px solid ${MP.borderSoft}`,
      fontSize: 13,
      color: MP.text,
      verticalAlign: 'middle',
      ...sx,
    }}
  >
    {children}
  </Box>
);

// Hover paints the ROW, not each cell: a `td` background sits above the
// row's own, so the old `&:hover td` rule meant a cell with its own tint
// (a highlighted amount, say) lost it on hover.
export const Tr = ({ children, onClick, sx = {} }) => (
  <Box
    component="tr"
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      transition: 'background .15s',
      '&:hover': { bgcolor: MP.rowHover },
      ...sx,
    }}
  >
    {children}
  </Box>
);
